import { useMemo } from 'react';
import { useAuth } from './index';
import { 
  getPermissions, 
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions,
  checkIsAdmin 
} from '../utils/auth';

export const usePermissions = () => {
  const { user } = useAuth();

  const permissions = useMemo(() => getPermissions(user), [user]);

  const checkPermission = (permissionKey: string) => {
    return hasPermission(user, permissionKey);
  };

  const checkAnyPermission = (permissionKeys: string[]) => {
    return hasAnyPermission(user, permissionKeys);
  };

  const checkAllPermissions = (permissionKeys: string[]) => {
    return hasAllPermissions(user, permissionKeys);
  };

  const isAdmin = useMemo(() => checkIsAdmin(user), [user]);

  return {
    permissions,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    isAdmin,
    /**
     * Helper for RTK Query skip option
     */
    getSkipOption: (permissionKey: string) => ({
      skip: !isAdmin && !checkPermission(permissionKey),
    }),
    /**
     * Wraps a mutation function to only execute if the user has permission
     */
    guardMutation: <T extends (...args: any[]) => any>(
      mutationFn: T,
      permissionKey: string
    ) => {
      return ((...args: Parameters<T>) => {
        if (isAdmin || checkPermission(permissionKey)) {
          return mutationFn(...args);
        }
        console.warn(`Mutation skipped: missing permission ${permissionKey}`);
        return undefined;
      }) as T;
    },
  };
};
